import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { LabraToolTipV2 } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUp,
  faStop,
  faPlus,
  faXmark,
  faBookmark,
} from '@fortawesome/pro-regular-svg-icons'
import {
  useLabraChatContext,
  useLabraChatDispatch,
  useLabraChatSelector,
} from '../../../modules/labraChat/context'
import {
  sendLabraChatPrompt,
  stopLabraChatMessage,
  setLabraChatComposerDraft,
} from '../../../modules/labraChat/actions'
import {
  selectIsLabraChatLoading,
  selectLabraChatComposerDraft,
} from '../../../modules/labraChat/selectors'
import { ChatUploadedDocument } from '../../../modules/labraChat/api'
import { useChatDocumentUpload } from '../../../modules/labraChat/useChatDocumentUpload'
import { SavedPromptsMenu } from '../SavedPrompts/SavedPromptsMenu'
import { useStyles } from './ChatInputBar.styles'

const MAX_TEXTAREA_HEIGHT = 120
const ACCEPTED_FILE_TYPES = 'application/pdf,.pdf'
/** Sent as the message when the user attaches docs without typing anything. */
const ATTACHMENT_ONLY_PROMPT =
  'Please review the attached document(s) for my funding request.'

/** "pricing_calculator" → "pricing calculator" (CSS upper-cases it). */
const docTypeLabel = (docType: string): string => docType.replace(/_/g, ' ')

interface ChatInputBarProps {
  compact?: boolean
  /** Overrides the default "Ask Labra AI Agent..." placeholder. */
  placeholder?: string
}

export const ChatInputBar: React.FC<ChatInputBarProps> = ({
  compact = false,
  placeholder = 'Ask Labra AI Agent...',
}) => {
  const { classes, cx } = useStyles()
  const dispatch = useLabraChatDispatch()
  const [inputValue, setInputValue] = useState('')
  const [attachments, setAttachments] = useState<ChatUploadedDocument[]>([])
  const [promptsAnchorEl, setPromptsAnchorEl] = useState<HTMLElement | null>(
    null
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { partnerId } = useLabraChatContext()
  const isLoading = useLabraChatSelector(selectIsLabraChatLoading)
  const composerDraft = useLabraChatSelector(selectLabraChatComposerDraft)
  const { uploadDocument, isUploading } = useChatDocumentUpload(partnerId)

  useEffect(() => {
    if (composerDraft == null) return
    setInputValue(composerDraft)
    dispatch(setLabraChatComposerDraft(null))
    // Focus after insert so the user can edit (Figma sample-prompt tooltip).
    requestAnimationFrame(() => textareaRef.current?.focus())
  }, [composerDraft, dispatch])

  // Auto-grow the textarea up to MAX_TEXTAREA_HEIGHT, then let it scroll.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`
  }, [inputValue])

  const handleFilesSelected = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList?.length) return
      // Upload sequentially so a presign failure on one doesn't abort the rest.
      for (const file of Array.from(fileList)) {
        const uploaded = await uploadDocument(file)
        if (uploaded) {
          setAttachments(prev => [...prev, uploaded])
        }
      }
    },
    [uploadDocument]
  )

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFilesSelected(event.target.files)
    // Reset so selecting the same file again still fires onChange.
    event.target.value = ''
  }

  const removeAttachment = (s3Uri: string) =>
    setAttachments(prev => prev.filter(doc => doc.s3_uri !== s3Uri))

  const hasContent = inputValue.trim().length > 0 || attachments.length > 0
  const canSend = hasContent && !isLoading && !isUploading

  // Only ever invoked when `canSend` is true (send button is disabled and the
  // Enter handler is gated on it), so no empty/loading guard is needed here.
  const handleSend = useCallback(() => {
    const typed = inputValue.trim()
    // Attachments with no message still need a prompt for the agent; the bubble
    // shows the files as pills (empty text), so don't echo a machine prompt.
    const message = typed || ATTACHMENT_ONLY_PROMPT
    const displayMessage = !typed && attachments.length ? '' : undefined

    dispatch(
      sendLabraChatPrompt(partnerId, message, {
        displayMessage,
        documents: attachments,
      })
    )
    setInputValue('')
    setAttachments([])
  }, [dispatch, inputValue, attachments, partnerId])

  const insertNewlineAtCursor = (target: HTMLTextAreaElement) => {
    const start = target.selectionStart ?? inputValue.length
    const end = target.selectionEnd ?? inputValue.length
    const nextValue = `${inputValue.slice(0, start)}\n${inputValue.slice(end)}`
    setInputValue(nextValue)
    // Restore the caret right after the inserted newline once React re-renders.
    requestAnimationFrame(() => {
      const caret = start + 1
      target.selectionStart = caret
      target.selectionEnd = caret
    })
  }

  const handleStop = useCallback(() => {
    dispatch(stopLabraChatMessage())
  }, [dispatch])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter') return

    // Cmd/Ctrl+Enter (and Shift+Enter) insert a newline instead of sending.
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault()
      insertNewlineAtCursor(event.currentTarget)
      return
    }
    if (event.shiftKey) return

    event.preventDefault()
    if (canSend) handleSend()
  }

  return (
    <div className={classes.wrapper}>
      {attachments.length > 0 && (
        <div className={classes.attachmentRow}>
          {attachments.map(doc => (
            <div key={doc.s3_uri} className={classes.attachmentChip}>
              <span className={classes.attachmentName} title={doc.filename}>
                {doc.filename}
              </span>
              <span className={classes.attachmentType}>
                {docTypeLabel(doc.doc_type)}
              </span>
              <button
                className={classes.attachmentRemove}
                type="button"
                onClick={() => removeAttachment(doc.s3_uri)}
                aria-label={`Remove ${doc.filename}`}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className={cx(classes.inputField, compact && classes.inputFieldCompact)}
      >
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          className={cx(classes.textInput, compact && classes.textInputCompact)}
        />

        <div
          className={cx(classes.actionRow, compact && classes.actionRowCompact)}
        >
          <div
            className={cx(
              classes.actionGroup,
              compact && classes.actionGroupCompact
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              multiple
              className={classes.hiddenFileInput}
              onChange={handleFileInputChange}
            />
            <LabraToolTipV2 tooltipPlacement="top" description="Add files">
              <button
                className={classes.actionButton}
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isLoading}
                aria-label="Add files"
              >
                {isUploading ? (
                  <CircularProgress size={12} thickness={5} />
                ) : (
                  <FontAwesomeIcon icon={faPlus} />
                )}
              </button>
            </LabraToolTipV2>
            <LabraToolTipV2 tooltipPlacement="top" description="Saved prompts">
              <button
                className={classes.actionButton}
                type="button"
                onClick={event => setPromptsAnchorEl(event.currentTarget)}
                aria-label="Saved prompts"
                aria-haspopup="menu"
                aria-expanded={!!promptsAnchorEl}
              >
                <FontAwesomeIcon icon={faBookmark} />
              </button>
            </LabraToolTipV2>
            <SavedPromptsMenu
              anchorEl={promptsAnchorEl}
              onClose={() => setPromptsAnchorEl(null)}
              onSelect={setInputValue}
            />
          </div>

          <button
            className={cx(
              classes.actionButton,
              compact && classes.sendButtonCompact
            )}
            onClick={isLoading ? handleStop : handleSend}
            type="button"
            disabled={!isLoading && !canSend}
            aria-label={isLoading ? 'Stop generating' : 'Send message'}
          >
            <FontAwesomeIcon icon={isLoading ? faStop : faArrowUp} />
          </button>
        </div>
      </div>
    </div>
  )
}
